import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { AuthUser } from '@app/account/account-api.model';
import { OrgMembersService } from '@app/orgs/services/org-members.service';
import { isKey, Key } from '@app/shared/key';
import { AuthService } from '@app/shared/services/auth.service';
import { tagHasIncompleteRatingsForType } from '@app/shared/utils/tag-helpers';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { UserProfileSummary } from '@app/user/user-api.model';
import { TranslateService } from '@ngx-translate/core';
import { ProfileSkillsListService } from '@app/profile/services/profile-skills-list.service';

@Component({
  selector: 'dgx-profile-skills-list-manager-rating',
  templateUrl: './profile-skills-list-manager-rating.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSkillsListManagerRatingComponent {
  @Input() public tag: TagsApi.TagDetails;
  @Input() public canRequestManagerRating: boolean;
  @Input() public isOwner: boolean;
  @Input() public showLock: boolean;
  @Output() public tagRatingChange = new EventEmitter<any>();

  public authUser: AuthUser;
  public i18n: { [key: string]: string } = {};

  public loading = false;

  constructor(
    private authService: AuthService,
    private tagRatingService: TagRatingService,
    private tagsService: TagsService,
    private translateService: TranslateService,
    private orgMembersService: OrgMembersService,
    private profileSkillsListService: ProfileSkillsListService
  ) {
    this.i18n = this.translateService.instant([
      'dgTagRating_ManagerAssessment',
      'dgTagRating_RequestManagerAssessment',
      'Core_Private',
      'Core_LimitedVisibility',
    ]);
  }

  public get level(): string | undefined {
    const rating = this.tag.ratings?.find(
      (rating) => rating.type === InternalTagRatingTypes.manager
    );
    return rating?.level;
  }

  public get inProgress(): boolean {
    return tagHasIncompleteRatingsForType(
      this.tag,
      InternalTagRatingTypes.manager
    );
  }

  public get showDonut(): boolean {
    return (
      (this.isOwner && this.canRequestManagerRating) ||
      !!this.level ||
      (this.isOwner && this.inProgress)
    );
  }

  public get showDefaultPlus(): boolean {
    return !this.level && this.isOwner && !this.inProgress;
  }

  public get tabIndex(): 0 | undefined {
    return this.isOwner ? 0 : undefined;
  }

  public get role(): 'button' | undefined {
    return this.isOwner ? 'button' : undefined;
  }

  public get ariaLabel(): string {
    let label = '';
    if (this.level) {
      label = this.translateService.instant(
        'dgTagRating_A11yRequestUpdateManagerRating',
        {
          skillName: this.tag.title,
          levelNumber: this.level,
        }
      );
    } else {
      label = this.translateService.instant(
        'dgTagRating_A11yRequestManagerRating',
        {
          skillName: this.tag.title,
        }
      );
    }
    label = this.profileSkillsListService.getAriaLabelText(
      label,
      this.privacyText,
      ''
    );
    return label;
  }

  public get tooltip(): string {
    return this.profileSkillsListService.getTooltipText(
      this.baseText,
      this.privacyText
    );
  }

  private get levelText(): string {
    return this.profileSkillsListService.getLevelText(this.level);
  }

  private get baseText() {
    return this.level
      ? this.i18n.dgTagRating_ManagerAssessment
      : this.i18n.dgTagRating_RequestManagerAssessment;
  }

  private get privacyText() {
    return this.profileSkillsListService.getPrivacyText(
      this.level,
      this.isOwner,
      this.showLock,
      this.authService.authUser?.participateInTeamProgram ?? false
    );
  }

  public ngOnInit(): void {
    this.authUser = this.authService.authUser;
  }

  public clickHandler(event: MouseEvent): void {
    this.eventHandler(event);
  }

  public keydownHandler(event: KeyboardEvent): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      // prevent spacebar from scrolling page
      event.preventDefault();
      this.eventHandler(event);
    }
  }

  private eventHandler(event: Event): void {
    if (this.loading) {
      return;
    }

    if (this.isOwner) {
      // Set this false after any of the modal box is opened

      this.loading = true;
      // prevent event from triggering skill modal
      event.stopPropagation();
      // Check to see if we already have a requested/completed manager rating
      this.tagsService
        .getTagRatingDetails(
          this.authUser.viewerProfile.userProfileKey,
          this.tag.tagId
        )
        .subscribe(
          (ratings: TagsApi.UserTagRatingDetails[]) => {
            const managerRatings = ratings.filter(
              (rating) => rating.type === InternalTagRatingTypes.manager
            );
            if (!managerRatings.length) {
              this.findManagerAndRequestRating(event);
            } else {
              const ratingsObj = managerRatings.reduce((acc, curr) => {
                if (curr.dateCompleted) {
                  acc.completed = curr;
                } else {
                  acc.pending = curr;
                }
                return acc;
              }, {} as unknown as any);
              if (ratingsObj.pending) {
                this.cancelRatingRequest(event);
              } else if (ratingsObj.completed) {
                this.viewCompletedManagerRating(event, ratingsObj.completed);
              }
            }
          },
          () => (this.loading = false)
        );
    }
  }

  private findManagerAndRequestRating(event: Event): void {
    this.orgMembersService
      .getManager(this.authService.authUser.defaultOrgId)
      .subscribe((manager: UserProfileSummary) => {
        this.tagRatingService
          .openManagerRatingRequestModal(event, this.tag, manager)
          .subscribe((result) => {
            this.tagRatingChange.emit({
              tag: this.tag,
              manager: result,
              action: 'pending',
            });
          });
        this.loading = false;
      });
  }

  private cancelRatingRequest(event: Event): void {
    this.tagRatingService
      .openCancelManagerRatingRequestModal(event, this.tag)
      .subscribe(() => {
        this.tagRatingChange.emit({
          tag: this.tag,
          manager: null,
          action: 'cancelPending',
        });
      });
    this.loading = false;
  }

  private viewCompletedManagerRating(
    event: Event,
    ratingInfo: Partial<TagsApi.UserTagRatingDetails>
  ): void {
    const allowRatingRequest = this.authUser?.canRequestManagerRating;

    this.orgMembersService
      .getManager(this.authService.authUser?.defaultOrgId)
      .subscribe((manager) => {
        // use manager if available, otherwise use previous rater
        const rater = manager || ratingInfo.rater;
        this.tagRatingService.openCompletedAssociateRatingModal(
          event,
          ratingInfo,
          allowRatingRequest,
          () =>
            this.tagRatingService
              .openManagerRatingRequestModal(event, this.tag, rater)
              .subscribe((result) => {
                this.tagRatingChange.emit({
                  tag: this.tag,
                  manager: ratingInfo,
                  action: 'newPending',
                });
              })
        );
        this.loading = false;
      });
  }

  public getLevelDescription(level: string): string {
    return this.tagsService.orgLevels.find((l) => l.value.toString() === level)
      ?.description;
  }
}
